/**
 * Card texture baking.
 *
 * Everything the card shader samples is produced here, once, at load time:
 *
 * 1. **Face atlas** — the 52 SVGs in `public/cards/` rasterised at device-pixel
 *    scale and composited onto a procedurally generated premium paper: rounded
 *    corners, a warm linen tooth, an edge vignette and a hairline rule. The
 *    source SVGs ship with their own flat-white body and a hard black outline;
 *    both are stripped so the paper underneath is ours.
 * 2. **Card back** — generated, never loaded. Deep plum field, a double
 *    guilloche rosette over a fine lattice, a thin gold border inset from the
 *    edge and a small centred `21` mark.
 * 3. **Normal atlas** — the reason this branch exists. A height field is built
 *    per cell (a bevel falling off over ~10 design units at the border, a very
 *    slight centre dome, and micro-noise for paper tooth) and differentiated
 *    into a tangent-space normal map. `filters/cardLighting.ts` lights it
 *    per-pixel against `LIGHT`. Card geometry is identical for all 52 faces, so
 *    only a handful of noise *variants* are baked and cards are assigned one by
 *    hash — the tooth differs between neighbours in a stack without paying for
 *    53 full-resolution normal cells.
 * 4. **Shadow stamps** — two blurred rounded rects (tight and soft) that
 *    `card.ts` crossfades as a card's virtual lift grows. Baked here so the
 *    whole card look comes out of one place.
 *
 * Two rules are load-bearing:
 * - fonts are loaded *before* anything is drawn, or the wordmark bakes a
 *   fallback face into the atlas permanently;
 * - every SVG is fully decoded before it is drawn, or cells come out blank.
 */

import { ImageSource, Rectangle, Texture, type TextureSource } from 'pixi.js';

import { createDeck } from '@/game/deck';
import { WILD_CARDS, isHiddenCard, type CardId } from '@/game/types';
import * as tokens from '@/core/tokens';
import { drawCardIndices } from '@/pixi/cardFace';
import { CARD, DESIGN, SHADOW } from '@/pixi/layout';
import { MAX_RESOLUTION, budgetedResolution } from '@/pixi/quality';

/** A normalised rect into one of the atlases: origin + size in 0..1 UV space. */
export interface CardUV {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface CardTextureOptions {
  /** Force the baked width of one card in texture pixels. Defaults to the display. */
  cellWidth?: number;
  /** Hard cap on either atlas dimension. */
  maxAtlasSize?: number;
}

export interface ShadowStamp {
  readonly texture: Texture;
  /** Size of the stamp expressed in design units, so `card.ts` can size the sprite. */
  readonly width: number;
  readonly height: number;
  /** The blur radius, in design units, this stamp was baked with. */
  readonly blur: number;
}

export interface CardAtlasStats {
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly faceAtlas: { width: number; height: number };
  readonly normalAtlas: { width: number; height: number };
  readonly normalVariants: number;
}

/* ------------------------------------------------------------------ *
 * Tuning
 * ------------------------------------------------------------------ */

/** How many distinct paper-tooth normal variants get baked. */
const NORMAL_VARIANTS = 4;

/**
 * Width of the bevel at the card border, in design units.
 *
 * Narrowed from 10. On a 200-unit card that was 5% of the width — about 3.2mm
 * of rolled edge on a 63mm card, where the real thing is nearer 0.15mm. At a
 * 400px render it spread the edge roll over a 20px gradient, which reads as a
 * soft pillow rather than a cut sheet, and once the specular lobe was tightened
 * that wide, gently-curving band started catching a broad highlight down one
 * side of every card.
 *
 * The angle was right; the width was not. `BEVEL_RISE` is raised to compensate
 * so the roll keeps roughly its original slope over a narrower run.
 */
const BEVEL_DESIGN = 4.5;

/** Peak height of the bevel as a fraction of its width — controls edge slope. */
const BEVEL_RISE = 0.55;

/** Centre dome height in design units. Deliberately tiny: cards are nearly flat. */
const DOME_DESIGN = 2.6;

/** Paper tooth amplitudes in design units, coarse and fine. */
const TOOTH_COARSE = 0.16;
const TOOTH_FINE = 0.055;

/**
 * Blur radii of the two baked shadow stamps, in design units, derived straight
 * from `SHADOW` so they bracket the blur range the lift model can ask for.
 * `card.ts` crossfades between them.
 */
const SHADOW_TIGHT_BLUR = SHADOW.blurBase;
const SHADOW_SOFT_BLUR = SHADOW.blurBase + SHADOW.blurPerLift * 110;

/** Card width in texture pixels used when baking the shadow stamps. */
const SHADOW_CARD_PX = 224;

const CARD_ASPECT = CARD.height / CARD.width;

const BASE_URL = import.meta.env.BASE_URL ?? '/';

const FONT_SOURCES = [
  { family: 'Bebas Neue', file: 'BebasNeue-Regular.ttf', weight: '400' },
  { family: 'Saira', file: 'Saira-Medium.ttf', weight: '500' },
  { family: 'Saira', file: 'Saira-Bold.ttf', weight: '700' },
] as const;

/* ------------------------------------------------------------------ *
 * Colour helpers — every value below is derived from @/core/tokens
 * ------------------------------------------------------------------ */

function channels(hex: number): [number, number, number] {
  return [(hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff];
}

function mixHex(a: number, b: number, t: number): number {
  const [ar, ag, ab] = channels(a);
  const [br, bg, bb] = channels(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function css(hex: number, alpha = 1): string {
  const [r, g, b] = channels(hex);
  return alpha >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Warm paper ramp: white pushed a few percent toward gold, cooled at the edge. */
const PAPER_HIGH = mixHex(tokens.palette.white, tokens.palette.gold, 0.03);
const PAPER_BASE = mixHex(tokens.palette.white, tokens.palette.gold, 0.1);
const PAPER_LOW = mixHex(mixHex(tokens.palette.white, tokens.palette.gold, 0.14), tokens.palette.plum, 0.09);

/* ------------------------------------------------------------------ *
 * Small canvas utilities
 * ------------------------------------------------------------------ */

function makeCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) throw new Error('cardTexture: 2D canvas context unavailable');
  return ctx;
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w * 0.5, h * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

/** Width of `text` with `tracking` px inserted between glyphs. */
function trackedWidth(ctx: CanvasRenderingContext2D, text: string, tracking: number): number {
  let total = 0;
  for (const ch of text) total += ctx.measureText(ch).width + tracking;
  return Math.max(0, total - tracking);
}

/**
 * Canvas has no reliable cross-browser letter-spacing, and the display face
 * needs real tracking or it reads as cramped — so glyphs are placed by hand.
 */
function drawTracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  tracking: number,
  mode: 'fill' | 'stroke' | 'both',
): void {
  const total = trackedWidth(ctx, text, tracking);
  const prevAlign = ctx.textAlign;
  const prevBaseline = ctx.textBaseline;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let x = cx - total * 0.5;
  for (const ch of text) {
    if (mode !== 'fill') ctx.strokeText(ch, x, cy);
    if (mode !== 'stroke') ctx.fillText(ch, x, cy);
    x += ctx.measureText(ch).width + tracking;
  }
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBaseline;
}

/* ------------------------------------------------------------------ *
 * Deterministic noise (shared by the paper grain and the normal map)
 * ------------------------------------------------------------------ */

function hash2(x: number, y: number, seed: number): number {
  let n = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(seed | 0, 362437);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Bilinear value noise on an integer lattice. Returns 0..1. */
function valueNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = smooth(x - xi);
  const fy = smooth(y - yi);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}

/* ------------------------------------------------------------------ *
 * Fonts
 * ------------------------------------------------------------------ */

let fontsPromise: Promise<void> | null = null;

/**
 * Load the display and body faces before any text is baked. Skipped silently in
 * environments without the CSS Font Loading API; the wordmark then falls back,
 * which is ugly but not fatal.
 */
function ensureFonts(): Promise<void> {
  if (fontsPromise) return fontsPromise;
  fontsPromise = (async () => {
    const set = typeof document === 'undefined' ? undefined : document.fonts;
    if (!set || typeof FontFace === 'undefined') return;
    await Promise.all(
      FONT_SOURCES.map(async (source) => {
        try {
          const face = new FontFace(source.family, `url(${BASE_URL}fonts/${source.file})`, {
            weight: source.weight,
            style: 'normal',
            display: 'block',
          });
          await face.load();
          set.add(face);
        } catch {
          /* A missing font must not take the whole preload down. */
        }
      }),
    );
    // Belt and braces: force the face resolution the canvas will ask for.
    await Promise.all([
      set.load(`400 64px ${tokens.type.display}`).catch(() => undefined),
      set.load(`500 64px ${tokens.type.body}`).catch(() => undefined),
    ]);
  })();
  return fontsPromise;
}

/* ------------------------------------------------------------------ *
 * SVG rasterisation
 * ------------------------------------------------------------------ */

/**
 * The shipped SVGs draw their own card body: flat `#FFFFFF` with a hard
 * `#231F20` outline, in a class named `.st0` or `.st0Red` depending on the
 * suit. Both are neutralised so only the ink art survives and our paper shows
 * through. If the rule ever changes shape the replacement simply misses and the
 * card still rasterises, just with the stock body.
 */
/**
 * The white card body each source file draws for itself.
 *
 * Exactly one path per file carries this style. It has to go: the atlas paints
 * its own warm paper with tooth and a vignette underneath, and an opaque white
 * rectangle on top of that hides all of it.
 *
 * Both spellings are matched because `tools/optimise-cards.mjs` normalises
 * `fill:#FFFFFF;stroke-width:0.5;` to `fill:#fff;stroke-width:.5`, so the rule
 * has to survive whether or not the deck has been through SVGO.
 */
const BODY_RULE = /fill\s*:\s*#(?:ffffff|fff)\s*;\s*stroke-width\s*:\s*0?\.5\s*;?/gi;

/**
 * The two corner rank letters, which the source draws as `<text>`.
 *
 * Also has to go, and for a subtler reason: an SVG rasterised through an
 * `Image` is a **separate document**, so it cannot see fonts loaded into this
 * one — it gets system fonts only. These files ask for Arial and Bitstream Vera
 * Sans, so the indices would resolve differently on Windows, macOS and Linux,
 * and on most Linux boxes neither exists. The letters are redrawn in the game's
 * own display face instead (see `drawCardIndices`), which is both consistent
 * across platforms and consistent with the HUD.
 *
 * Only the letters are text — every pip, court and corner suit is vector, and
 * all of that is kept.
 */
const TEXT_RULE = /<text\b[\s\S]*?<\/text>/gi;

/**
 * A decorative off-white plaque behind the ace of clubs' pip.
 *
 * `#fffeff` — one green step off white, which is why it slips past
 * `BODY_RULE` — and it appears in exactly one file in the deck. It is a
 * deliberate part of that card's design against a white body, but against this
 * game's warm paper it reads as a bright rectangle pasted on the card. Verified
 * unique before stripping: no other file uses this colour, and no court
 * illustration depends on it.
 */
const PLAQUE_RULE = /fill\s*:\s*#fffeff\s*;/gi;

function prepareSvg(source: string, width: number, height: number): string {
  let svg = source.replace(BODY_RULE, 'fill:none;stroke:none;');
  svg = svg.replace(PLAQUE_RULE, 'fill:none;');
  svg = svg.replace(TEXT_RULE, '');

  // Retarget the ROOT element only, and drop the size it already carries.
  //
  // `meet`, not `none`. The source files are 167.09 x 242.67, an aspect of
  // 1.4523, and the cell they are drawn into is CARD's 1.4010 — so stretching
  // one to fill the other made every pip, every court figure and every suit
  // 3.7% wider than it should be. That is the same distortion the card's own
  // proportions were corrected to remove, reintroduced one layer further in,
  // and on a round pip it is visible. `meet` fits the art inside the cell at
  // its true proportions and centres it, which costs 1.7% of the cell's width
  // as margin and is the trade worth making: the card outline is ours to
  // choose, the artwork's proportions are not.
  //
  // These files declare `width="167.0869141pt" height="242.6669922pt"`. Simply
  // prepending our own would leave two of each attribute, which is invalid XML
  // — the browser rejects the whole document and the decode fails with nothing
  // more useful than "the source image cannot be decoded". `viewBox` is left
  // alone; it is what the new size scales against.
  svg = svg.replace(/<svg\b[^>]*>/i, (tag) => {
    const stripped = tag
      .replace(/\swidth\s*=\s*"[^"]*"/gi, '')
      .replace(/\sheight\s*=\s*"[^"]*"/gi, '')
      .replace(/\spreserveAspectRatio\s*=\s*"[^"]*"/gi, '');
    return stripped.replace(
      /^<svg/i,
      `<svg width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet"`,
    );
  });

  return svg;
}

async function rasteriseSvg(id: CardId, width: number, height: number): Promise<HTMLImageElement> {
  const response = await fetch(`${BASE_URL}cards/${id}.svg`);
  if (!response.ok) throw new Error(`cardTexture: could not load ${id}.svg (${response.status})`);
  const svg = prepareSvg(await response.text(), width, height);
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const image = new Image();
    image.decoding = 'async';
    image.width = width;
    image.height = height;
    image.src = url;
    // decode() is the only thing that guarantees the bitmap exists before we
    // draw it; `onload` alone can still hand back an undecoded SVG.
    await image.decode();
    return image;
  } finally {
    // Revoking after decode is safe — the bitmap is already in memory.
    URL.revokeObjectURL(url);
  }
}

/* ------------------------------------------------------------------ *
 * Paper treatment
 * ------------------------------------------------------------------ */

let grainPattern: CanvasPattern | null = null;

function getGrainPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
  if (grainPattern) return grainPattern;
  const size = 128;
  const tile = makeCanvas(size, size);
  const tileCtx = context2d(tile);
  const image = tileCtx.createImageData(size, size);
  const data = image.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Two octaves of tooth plus a faint woven ripple in both directions.
      const n = valueNoise(x * 0.9, y * 0.9, 17) * 0.6 + valueNoise(x * 0.28, y * 0.28, 91) * 0.4;
      const weave = (Math.sin(x * 1.05) + Math.sin(y * 1.05)) * 0.06;
      const v = Math.max(0, Math.min(1, n + weave));
      const level = Math.round(120 + v * 135);
      const i = (y * size + x) * 4;
      data[i] = level;
      data[i + 1] = level;
      data[i + 2] = level;
      data[i + 3] = 255;
    }
  }
  tileCtx.putImageData(image, 0, 0);
  grainPattern = ctx.createPattern(tile, 'repeat');
  return grainPattern;
}

/** Warm paper field, linen tooth and a soft inward vignette. Assumes a clip. */
function paintPaper(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const field = ctx.createLinearGradient(0, 0, w * 0.35, h);
  field.addColorStop(0, css(PAPER_HIGH));
  field.addColorStop(0.55, css(PAPER_BASE));
  field.addColorStop(1, css(PAPER_LOW));
  ctx.fillStyle = field;
  ctx.fillRect(0, 0, w, h);

  const grain = getGrainPattern(ctx);
  if (grain) {
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = grain;
    // Scale the tile with the bake so tooth density is resolution independent.
    const scale = w / 320;
    ctx.scale(scale, scale);
    ctx.fillRect(0, 0, w / scale, h / scale);
    ctx.restore();
  }

  // Edge vignette: four directional falloffs, tinted with the felt's plum so the
  // card sits in the room rather than floating on it.
  const band = Math.max(6, w * 0.16);
  const edge = css(tokens.palette.plum, 0.16);
  const clear = css(tokens.palette.plum, 0);
  const sides: Array<[number, number, number, number, number, number, number, number]> = [
    [0, 0, 0, band, 0, 0, w, band],
    [0, h, 0, h - band, 0, h - band, w, band],
    [0, 0, band, 0, 0, 0, band, h],
    [w, 0, w - band, 0, w - band, 0, band, h],
  ];
  for (const [gx0, gy0, gx1, gy1, rx, ry, rw, rh] of sides) {
    const gradient = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
    gradient.addColorStop(0, edge);
    gradient.addColorStop(1, clear);
    ctx.fillStyle = gradient;
    ctx.fillRect(rx, ry, rw, rh);
  }
}

/** The thin printed rule just inside the card edge, plus a crisp lit lip. */
function paintCardRules(ctx: CanvasRenderingContext2D, w: number, h: number, radius: number): void {
  const inset = w * 0.032;
  ctx.lineWidth = Math.max(1, w * 0.0045);
  ctx.strokeStyle = css(tokens.palette.ink, 0.1);
  roundRectPath(ctx, inset, inset, w - inset * 2, h - inset * 2, radius * 0.72);
  ctx.stroke();

  // A faint burnish on the cut corner, not a lit lip.
  //
  // This was 0.5 alpha, which made a ~1.2px 50%-white hairline the brightest
  // element on the card — and, being baked, it was identical on all four sides.
  // Under a key raking from the upper left the bottom-right rim should be in
  // shadow; a direction-independent bright outline is the fastest way to undo a
  // directional lighting model, because the eye reads a uniform outline as a
  // sticker. The bevel in the normal map already produces the lit lip
  // correctly and directionally, and does it far better now that the shader has
  // a Fresnel term for the bevel normal to catch.
  ctx.lineWidth = Math.max(1, w * 0.006);
  ctx.strokeStyle = css(tokens.palette.white, 0.14);
  roundRectPath(ctx, 0.5, 0.5, w - 1, h - 1, radius);
  ctx.stroke();
}

/* ------------------------------------------------------------------ *
 * Wild treatment (baked half — the animated half lives in filters/foil.ts)
 * ------------------------------------------------------------------ */

/**
 * The static foil layer for J♠ and J♣ only. J♥ and J♦ are ordinary tens and
 * never see this function — confusing them is a gameplay error, not a
 * cosmetic one, so the caller checks `WILD_CARDS`, not the rank.
 */
function paintWildTreatment(ctx: CanvasRenderingContext2D, w: number, h: number, radius: number): void {
  // A cold-to-warm wash that gives the animated foil something to sit on. It
  // multiplies, so the printed pips keep their contrast instead of bleaching.
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  const wash = ctx.createLinearGradient(0, 0, w, h);
  wash.addColorStop(0, css(mixHex(tokens.palette.white, tokens.palette.violetLight, 0.34)));
  wash.addColorStop(0.42, css(mixHex(tokens.palette.white, tokens.palette.mint, 0.2)));
  wash.addColorStop(0.72, css(mixHex(tokens.palette.white, tokens.palette.gold, 0.34)));
  wash.addColorStop(1, css(mixHex(tokens.palette.white, tokens.palette.violet, 0.28)));
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  // Baked lenticular ruling. The shader adds a moving one on top; this is the
  // static ink underneath so the card still reads as foil when frozen.
  ctx.save();
  ctx.globalCompositeOperation = 'overlay';
  ctx.lineWidth = Math.max(1, w * 0.0035);
  ctx.strokeStyle = css(tokens.palette.violetLight, 0.16);
  const step = Math.max(3, w * 0.018);
  ctx.beginPath();
  for (let x = -h; x < w + h; x += step) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x + h * 0.42, h);
  }
  ctx.stroke();
  ctx.restore();

  // The wordmark sits in the empty band between the corner rank and the big
  // centre pip, so it never fights the art.
  const bandY = h * 0.355;
  const bandH = h * 0.115;
  ctx.save();
  const plate = ctx.createLinearGradient(0, bandY - bandH * 0.5, 0, bandY + bandH * 0.5);
  plate.addColorStop(0, css(tokens.palette.plum, 0.0));
  plate.addColorStop(0.5, css(tokens.palette.plum, 0.62));
  plate.addColorStop(1, css(tokens.palette.plum, 0.0));
  ctx.fillStyle = plate;
  ctx.fillRect(0, bandY - bandH * 0.5, w, bandH);

  ctx.lineWidth = Math.max(1, w * 0.005);
  ctx.strokeStyle = css(tokens.palette.gold, 0.7);
  ctx.beginPath();
  ctx.moveTo(w * 0.12, bandY - bandH * 0.42);
  ctx.lineTo(w * 0.88, bandY - bandH * 0.42);
  ctx.moveTo(w * 0.12, bandY + bandH * 0.42);
  ctx.lineTo(w * 0.88, bandY + bandH * 0.42);
  ctx.stroke();

  const size = Math.round(h * 0.082);
  ctx.font = `400 ${size}px ${tokens.type.display}`;
  const tracking = size * 0.2;
  const ink = ctx.createLinearGradient(w * 0.16, 0, w * 0.84, 0);
  ink.addColorStop(0, css(tokens.palette.violetLight));
  ink.addColorStop(0.4, css(tokens.palette.white));
  ink.addColorStop(0.74, css(tokens.palette.gold));
  ink.addColorStop(1, css(tokens.palette.violetLight));
  ctx.strokeStyle = css(tokens.palette.ink, 0.85);
  ctx.lineWidth = Math.max(1.5, size * 0.13);
  ctx.lineJoin = 'round';
  ctx.fillStyle = ink;
  drawTracked(ctx, 'WILD', w * 0.5, bandY, tracking, 'both');
  ctx.restore();

  // A violet inner rim so the card reads as wild even before the shader runs.
  ctx.save();
  ctx.lineWidth = Math.max(1, w * 0.014);
  ctx.strokeStyle = css(tokens.palette.violet, 0.42);
  roundRectPath(ctx, ctx.lineWidth * 0.5, ctx.lineWidth * 0.5, w - ctx.lineWidth, h - ctx.lineWidth, radius);
  ctx.stroke();
  ctx.restore();
}

/* ------------------------------------------------------------------ *
 * Card back — fully procedural
 * ------------------------------------------------------------------ */

/** Hypotrochoid, the curve behind every banknote guilloche. */
function guilloche(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  teeth: number,
  depth: number,
  steps: number,
): void {
  const r = radius / teeth;
  const a = radius - r;
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * Math.PI * 2;
    const k = (a / r) * t;
    const x = cx + a * Math.cos(t) + depth * r * Math.cos(k);
    const y = cy + a * Math.sin(t) - depth * r * Math.sin(k);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.stroke();
}

function paintBack(ctx: CanvasRenderingContext2D, w: number, h: number, radius: number): void {
  const cx = w * 0.5;
  const cy = h * 0.5;

  const field = ctx.createRadialGradient(cx, cy * 0.86, w * 0.05, cx, cy, h * 0.72);
  field.addColorStop(0, css(mixHex(tokens.palette.plum, tokens.palette.violet, 0.22)));
  field.addColorStop(0.55, css(tokens.palette.felt));
  field.addColorStop(1, css(tokens.palette.feltDeep));
  ctx.fillStyle = field;
  ctx.fillRect(0, 0, w, h);

  // Fine geometric lattice.
  ctx.save();
  ctx.lineWidth = Math.max(1, w * 0.0028);
  ctx.strokeStyle = css(tokens.palette.violetLight, 0.09);
  const step = w / 15;
  ctx.beginPath();
  for (let x = -h; x < w + h; x += step) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x + h, h);
    ctx.moveTo(x, 0);
    ctx.lineTo(x - h, h);
  }
  ctx.stroke();
  ctx.restore();

  // Nested guilloche rosettes.
  ctx.save();
  ctx.lineWidth = Math.max(1, w * 0.0032);
  ctx.strokeStyle = css(tokens.palette.gold, 0.2);
  guilloche(ctx, cx, cy, w * 0.42, 7, 0.85, 1400);
  ctx.strokeStyle = css(tokens.palette.violetLight, 0.24);
  guilloche(ctx, cx, cy, w * 0.34, 11, 0.7, 1600);
  ctx.strokeStyle = css(tokens.palette.gold, 0.14);
  guilloche(ctx, cx, cy, w * 0.24, 5, 0.95, 1000);
  ctx.restore();

  // Thin gold border, inset from the edge, with a hairline companion.
  const inset = w * 0.055;
  ctx.lineWidth = Math.max(1, w * 0.007);
  ctx.strokeStyle = css(tokens.palette.gold, 0.55);
  roundRectPath(ctx, inset, inset, w - inset * 2, h - inset * 2, radius * 0.6);
  ctx.stroke();

  ctx.lineWidth = Math.max(1, w * 0.003);
  ctx.strokeStyle = css(tokens.palette.gold, 0.2);
  const inner = inset * 1.75;
  roundRectPath(ctx, inner, inner, w - inner * 2, h - inner * 2, radius * 0.46);
  ctx.stroke();

  // The centred mark: a gold lozenge carrying the house number.
  const markR = w * 0.145;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(Math.PI * 0.25);
  const plate = ctx.createLinearGradient(-markR, -markR, markR, markR);
  plate.addColorStop(0, css(mixHex(tokens.palette.plum, tokens.palette.violet, 0.45)));
  plate.addColorStop(1, css(tokens.palette.feltDeep));
  ctx.fillStyle = plate;
  ctx.fillRect(-markR, -markR, markR * 2, markR * 2);
  ctx.lineWidth = Math.max(1, w * 0.008);
  ctx.strokeStyle = css(tokens.palette.gold, 0.8);
  ctx.strokeRect(-markR, -markR, markR * 2, markR * 2);
  ctx.lineWidth = Math.max(1, w * 0.003);
  ctx.strokeStyle = css(tokens.palette.gold, 0.32);
  ctx.strokeRect(-markR * 0.78, -markR * 0.78, markR * 1.56, markR * 1.56);
  ctx.restore();

  const size = Math.round(w * 0.15);
  ctx.font = `400 ${size}px ${tokens.type.display}`;
  ctx.fillStyle = css(tokens.palette.gold, 0.92);
  drawTracked(ctx, '21', cx, cy + size * 0.02, size * 0.06, 'fill');

  // Vignette so the back's edges fall away exactly like the faces'.
  const band = Math.max(6, w * 0.2);
  const shade = css(tokens.palette.void, 0.55);
  const clear = css(tokens.palette.void, 0);
  const sides: Array<[number, number, number, number, number, number, number, number]> = [
    [0, 0, 0, band, 0, 0, w, band],
    [0, h, 0, h - band, 0, h - band, w, band],
    [0, 0, band, 0, 0, 0, band, h],
    [w, 0, w - band, 0, w - band, 0, band, h],
  ];
  for (const [gx0, gy0, gx1, gy1, rx, ry, rw, rh] of sides) {
    const gradient = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
    gradient.addColorStop(0, shade);
    gradient.addColorStop(1, clear);
    ctx.fillStyle = gradient;
    ctx.fillRect(rx, ry, rw, rh);
  }
}

/* ------------------------------------------------------------------ *
 * Normal map generation
 * ------------------------------------------------------------------ */

/** Signed distance from the rounded-rect border, positive inside. */
function insideDistance(px: number, py: number, w: number, h: number, r: number): number {
  const nearLeft = px < r;
  const nearRight = px > w - r;
  const nearTop = py < r;
  const nearBottom = py > h - r;
  if ((nearLeft || nearRight) && (nearTop || nearBottom)) {
    const cx = nearLeft ? r : w - r;
    const cy = nearTop ? r : h - r;
    return r - Math.hypot(px - cx, py - cy);
  }
  return Math.min(px, w - px, py, h - py);
}

/**
 * Build one tangent-space normal cell.
 *
 * The height field is expressed in the same units as x and y, so the finite
 * differences below are true slopes and the bevel angle is exactly what the
 * constants say it is. Outside the rounded rect the normal is flat, which keeps
 * linear filtering from dragging bevel normals into the transparent corners.
 */
function buildNormalCell(
  target: ImageData,
  width: number,
  height: number,
  radius: number,
  bevel: number,
  dome: number,
  toothCoarse: number,
  toothFine: number,
  seed: number,
): void {
  const heights = new Float32Array(width * height);
  const inside = new Uint8Array(width * height);
  const rise = bevel * BEVEL_RISE;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const d = insideDistance(x + 0.5, y + 0.5, width, height, radius);
      if (d <= 0) {
        heights[i] = 0;
        continue;
      }
      inside[i] = 1;
      const t = Math.min(1, d / bevel);
      let z = smooth(t) * rise;
      // A very slight dome: the card is not perfectly flat.
      const u = (x + 0.5) / width * 2 - 1;
      const v = (y + 0.5) / height * 2 - 1;
      z += (1 - u * u) * (1 - v * v) * dome;
      // Paper tooth, two octaves, faded out across the bevel so the edge stays clean.
      const toothFade = smooth(Math.min(1, d / (bevel * 0.8)));
      z += (valueNoise(x * 0.34, y * 0.34, seed) - 0.5) * toothCoarse * 2 * toothFade;
      z += (valueNoise(x * 1.15, y * 1.15, seed + 7) - 0.5) * toothFine * 2 * toothFade;
      heights[i] = z;
    }
  }

  const data = target.data;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const o = i * 4;
      if (!inside[i]) {
        data[o] = 128;
        data[o + 1] = 128;
        data[o + 2] = 255;
        data[o + 3] = 255;
        continue;
      }
      const hasLeft = x > 0;
      const hasRight = x < width - 1;
      const hasUp = y > 0;
      const hasDown = y < height - 1;
      const xm = hasLeft ? i - 1 : i;
      const xp = hasRight ? i + 1 : i;
      const ym = hasUp ? i - width : i;
      const yp = hasDown ? i + width : i;
      const spanX = Math.max(1, (hasLeft ? 1 : 0) + (hasRight ? 1 : 0));
      const spanY = Math.max(1, (hasUp ? 1 : 0) + (hasDown ? 1 : 0));
      const dx = (heights[xp] - heights[xm]) / spanX;
      const dy = (heights[yp] - heights[ym]) / spanY;
      // Screen space is y-down and so is the light, so no sign flip is needed.
      let nx = -dx;
      let ny = -dy;
      const nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len;
      ny /= len;
      data[o] = Math.round((nx * 0.5 + 0.5) * 255);
      data[o + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      data[o + 2] = Math.round((1 / len * 0.5 + 0.5) * 255);
      data[o + 3] = 255;
    }
  }
}

/* ------------------------------------------------------------------ *
 * Atlas layout
 * ------------------------------------------------------------------ */

interface GridLayout {
  cols: number;
  rows: number;
  cellW: number;
  cellH: number;
  pad: number;
  width: number;
  height: number;
}

function planGrid(count: number, cellW: number, maxDim: number, maxPixels: number): GridLayout {
  let w = cellW;
  for (let attempt = 0; attempt < 24; attempt++) {
    const h = Math.round(w * CARD_ASPECT);
    const pad = 4;
    let best: GridLayout | null = null;
    for (let cols = 1; cols <= count; cols++) {
      const rows = Math.ceil(count / cols);
      const width = cols * (w + pad) + pad;
      const height = rows * (h + pad) + pad;
      if (width > maxDim || height > maxDim || width * height > maxPixels) continue;
      if (!best || Math.max(width, height) < Math.max(best.width, best.height)) {
        best = { cols, rows, cellW: w, cellH: h, pad, width, height };
      }
    }
    if (best) return best;
    w = Math.floor(w * 0.88);
    if (w < 96) break;
  }
  throw new Error('cardTexture: could not fit a card atlas within the texture limits');
}

/**
 * Pick a bake resolution that comfortably covers the card's on-screen size.
 *
 * The fit has to be taken against the **live** design rect. This read
 * `min(vw / 1920, vh / 1080)` — the landscape rect, hardcoded — which on a
 * portrait phone is not the rect being drawn and not even close: a 390x844
 * viewport fits the portrait rect at 0.444 but scores 0.203 against 1920x1080,
 * so the bake was sized for a card less than half the one on screen and only
 * the 256 floor stopped it being far worse. Every card was then upscaled, which
 * is exactly the softness you notice most on the stock, because that is the one
 * card that sits still long enough to be read.
 *
 * The DPR clamp moved with it. Holding it at 2 while the renderer draws at 3
 * throws away a third of the linear resolution a second time over.
 *
 * ## Why the display and not the window
 *
 * The atlas is baked once, at boot, and never again — rebaking means
 * rasterising 52 SVGs on the main thread, which is a freeze, and a freeze is a
 * worse bug than the one it would fix. So the size it is baked at has to hold
 * for the whole session, and the window does not: a player who opens the game
 * in a small window and then maximises is drawing cards from an atlas baked for
 * a board a fraction of the size, upscaled by anything up to ~1.9x on a
 * high-DPI Windows desktop. That was reported as "the cards look a little
 * blurry", and the reporter mentioned maximising a small window in the same
 * breath without connecting the two.
 *
 * The display is the bound the window cannot exceed, so baking against it makes
 * the boot size irrelevant. The cost falls on one case — a small window on a
 * large display pays for detail it is not currently showing — and is bounded by
 * the 640 clamp here and the atlas caps in `planGrid`. That is the right way
 * round: a fixed memory cost on a large machine, rather than a visible defect
 * on an ordinary one.
 */
function pickCellWidth(): number {
  const vw = globalThis.innerWidth || DESIGN.width;
  const vh = globalThis.innerHeight || DESIGN.height;
  const { width: dw, height: dh } = displayRect(vw, vh);
  const fit = Math.min(dw / DESIGN.width, dh / DESIGN.height) || 1;
  // The resolution the renderer will actually use, not the display's raw DPR.
  // Baking for 3x while drawing at 2x is a 2.25x bigger atlas whose extra
  // detail is resolved away before it reaches a pixel — memory and load time on
  // the device least able to spare either.
  const resolution = budgetedResolution(dw, dh, MAX_RESOLUTION);
  const wanted = CARD.width * fit * resolution * 1.15;
  return Math.max(256, Math.min(640, Math.ceil(wanted / 32) * 32));
}

/**
 * The largest viewport this session could be asked to draw, in CSS pixels.
 *
 * `screen` is reported in the display's own orientation and does not turn with
 * the device, so a portrait phone reports 393x852 through one API and 852x393
 * through the other. Taking the sides in the order the *viewport* is currently
 * in keeps the fit meaningful across a rotation — otherwise a phone turning
 * sideways would compute a fit against a rect it is not drawing.
 *
 * Never smaller than the window: browsers that hide `screen`, and the odd
 * embedded view whose frame is larger than the display it claims, both end up
 * here and both are better served by the size actually being rendered.
 */
export function displayRect(vw: number, vh: number): { width: number; height: number } {
  const screen = globalThis.screen;
  const a = screen?.width ?? 0;
  const b = screen?.height ?? 0;
  const portrait = vh >= vw;
  const width = Math.max(vw, portrait ? Math.min(a, b) : Math.max(a, b));
  const height = Math.max(vh, portrait ? Math.max(a, b) : Math.min(a, b));
  return { width, height };
}

function uvRect(
  col: number,
  row: number,
  layout: GridLayout,
  atlasW: number,
  atlasH: number,
): CardUV {
  const px = layout.pad + col * (layout.cellW + layout.pad);
  const py = layout.pad + row * (layout.cellH + layout.pad);
  // Half-texel inset stops linear filtering from bleeding neighbouring cells in.
  const ix = 0.5 / atlasW;
  const iy = 0.5 / atlasH;
  return {
    x: px / atlasW + ix,
    y: py / atlasH + iy,
    w: layout.cellW / atlasW - ix * 2,
    h: layout.cellH / atlasH - iy * 2,
  };
}

function cellOrigin(index: number, layout: GridLayout): { x: number; y: number } {
  const col = index % layout.cols;
  const row = Math.floor(index / layout.cols);
  return {
    x: layout.pad + col * (layout.cellW + layout.pad),
    y: layout.pad + row * (layout.cellH + layout.pad),
  };
}

/* ------------------------------------------------------------------ *
 * Shadow stamps
 * ------------------------------------------------------------------ */

function bakeShadow(blurDesign: number): ShadowStamp {
  const res = SHADOW_CARD_PX / CARD.width;
  const cardW = SHADOW_CARD_PX;
  const cardH = Math.round(CARD.height * res);
  const blur = blurDesign * res;
  const pad = Math.ceil(blur * 2.2 + 4);
  const canvas = makeCanvas(cardW + pad * 2, cardH + pad * 2);
  const ctx = context2d(canvas);
  // `shadowBlur` is the one blur primitive every browser has had forever, so the
  // shape is drawn far off-canvas and only its shadow lands in frame.
  const offset = canvas.width * 4;
  ctx.shadowColor = css(tokens.palette.void, 1);
  ctx.shadowBlur = blur;
  ctx.shadowOffsetX = offset;
  ctx.fillStyle = css(tokens.palette.void, 1);
  roundRectPath(ctx, pad - offset, pad, cardW, cardH, CARD.cornerRadius * res);
  ctx.fill();
  const source = new ImageSource({
    resource: canvas,
    label: `card-shadow-${Math.round(blurDesign)}`,
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
  });
  return {
    texture: new Texture({ source, label: `card-shadow-${Math.round(blurDesign)}` }),
    width: canvas.width / res,
    height: canvas.height / res,
    blur: blurDesign,
  };
}

/* ------------------------------------------------------------------ *
 * Build
 * ------------------------------------------------------------------ */

interface CardAtlas {
  faceSource: TextureSource;
  normalSource: TextureSource;
  faceUVs: Map<CardId, CardUV>;
  faceTextures: Map<CardId, Texture>;
  backUV: CardUV;
  backTexture: Texture;
  normalUVs: CardUV[];
  normalIndex: Map<string, number>;
  shadowTight: ShadowStamp;
  shadowSoft: ShadowStamp;
  stats: CardAtlasStats;
}

let atlas: CardAtlas | null = null;
let building: Promise<void> | null = null;

function hashId(id: string): number {
  let n = 2166136261;
  for (let i = 0; i < id.length; i++) {
    n ^= id.charCodeAt(i);
    n = Math.imul(n, 16777619);
  }
  return n >>> 0;
}

async function build(options: CardTextureOptions): Promise<void> {
  if (typeof document === 'undefined') {
    throw new Error('cardTexture: preloadCardTextures() requires a DOM');
  }
  await ensureFonts();

  const ids = createDeck();
  const cellWidth = options.cellWidth ?? pickCellWidth();
  const maxDim = options.maxAtlasSize ?? 4096;
  // Cell 0 is the back; 1..52 are the faces.
  const layout = planGrid(ids.length + 1, cellWidth, maxDim, 13_000_000);

  const faceCanvas = makeCanvas(layout.width, layout.height);
  const faceCtx = context2d(faceCanvas);
  const radius = (CARD.cornerRadius / CARD.width) * layout.cellW;

  // --- back -------------------------------------------------------------
  {
    const { x, y } = cellOrigin(0, layout);
    faceCtx.save();
    faceCtx.translate(x, y);
    roundRectPath(faceCtx, 0, 0, layout.cellW, layout.cellH, radius);
    faceCtx.clip();
    paintBack(faceCtx, layout.cellW, layout.cellH, radius);
    faceCtx.restore();
  }

  // --- faces ------------------------------------------------------------
  const CHUNK = 8;
  for (let start = 0; start < ids.length; start += CHUNK) {
    const slice = ids.slice(start, start + CHUNK);
    const images = await Promise.all(
      slice.map((id) => rasteriseSvg(id, layout.cellW, layout.cellH)),
    );
    for (let i = 0; i < slice.length; i++) {
      const id = slice[i];
      const image = images[i];
      const { x, y } = cellOrigin(start + i + 1, layout);
      faceCtx.save();
      faceCtx.translate(x, y);
      roundRectPath(faceCtx, 0, 0, layout.cellW, layout.cellH, radius);
      faceCtx.clip();
      paintPaper(faceCtx, layout.cellW, layout.cellH);
      faceCtx.drawImage(image, 0, 0, layout.cellW, layout.cellH);
      // The corner letters are ours, not the file's — see `TEXT_RULE`.
      drawCardIndices(faceCtx, layout.cellW, layout.cellH, id, tokens.type.display);
      if (WILD_CARDS.includes(id)) {
        paintWildTreatment(faceCtx, layout.cellW, layout.cellH, radius);
      }
      paintCardRules(faceCtx, layout.cellW, layout.cellH, radius);
      faceCtx.restore();
    }
  }

  // --- normal atlas -----------------------------------------------------
  const normalCols = 2;
  const normalRows = Math.ceil(NORMAL_VARIANTS / normalCols);
  const normalLayout: GridLayout = {
    cols: normalCols,
    rows: normalRows,
    cellW: layout.cellW,
    cellH: layout.cellH,
    pad: layout.pad,
    width: normalCols * (layout.cellW + layout.pad) + layout.pad,
    height: normalRows * (layout.cellH + layout.pad) + layout.pad,
  };
  const normalCanvas = makeCanvas(normalLayout.width, normalLayout.height);
  const normalCtx = context2d(normalCanvas);
  normalCtx.fillStyle = 'rgb(128, 128, 255)';
  normalCtx.fillRect(0, 0, normalCanvas.width, normalCanvas.height);

  const scale = layout.cellW / CARD.width;
  const cell = normalCtx.createImageData(layout.cellW, layout.cellH);
  const normalUVs: CardUV[] = [];
  for (let v = 0; v < NORMAL_VARIANTS; v++) {
    buildNormalCell(
      cell,
      layout.cellW,
      layout.cellH,
      radius,
      BEVEL_DESIGN * scale,
      DOME_DESIGN * scale,
      TOOTH_COARSE * scale,
      TOOTH_FINE * scale,
      1013 + v * 7919,
    );
    const { x, y } = cellOrigin(v, normalLayout);
    normalCtx.putImageData(cell, x, y);
    normalUVs.push(
      uvRect(v % normalCols, Math.floor(v / normalCols), normalLayout, normalCanvas.width, normalCanvas.height),
    );
  }

  // --- upload -----------------------------------------------------------
  const faceSource = new ImageSource({
    resource: faceCanvas,
    label: 'card-face-atlas',
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
    autoGenerateMipmaps: false,
  });
  const normalSource = new ImageSource({
    resource: normalCanvas,
    label: 'card-normal-atlas',
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
    autoGenerateMipmaps: false,
    // The normal atlas is data, not colour: it must never be premultiplied.
    alphaMode: 'no-premultiply-alpha',
  });

  const faceUVs = new Map<CardId, CardUV>();
  const faceTextures = new Map<CardId, Texture>();
  const backOrigin = cellOrigin(0, layout);
  const backUV = uvRect(0, 0, layout, faceCanvas.width, faceCanvas.height);
  const backTexture = new Texture({
    source: faceSource,
    label: 'card-back',
    frame: new Rectangle(backOrigin.x, backOrigin.y, layout.cellW, layout.cellH),
  });

  const normalIndex = new Map<string, number>();
  normalIndex.set('back', hashId('back') % NORMAL_VARIANTS);

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const index = i + 1;
    const col = index % layout.cols;
    const row = Math.floor(index / layout.cols);
    faceUVs.set(id, uvRect(col, row, layout, faceCanvas.width, faceCanvas.height));
    const origin = cellOrigin(index, layout);
    faceTextures.set(
      id,
      new Texture({
        source: faceSource,
        label: `card-${id}`,
        frame: new Rectangle(origin.x, origin.y, layout.cellW, layout.cellH),
      }),
    );
    normalIndex.set(id, hashId(id) % NORMAL_VARIANTS);
  }

  atlas = {
    faceSource,
    normalSource,
    faceUVs,
    faceTextures,
    backUV,
    backTexture,
    normalUVs,
    normalIndex,
    shadowTight: bakeShadow(SHADOW_TIGHT_BLUR),
    shadowSoft: bakeShadow(SHADOW_SOFT_BLUR),
    stats: {
      cellWidth: layout.cellW,
      cellHeight: layout.cellH,
      faceAtlas: { width: faceCanvas.width, height: faceCanvas.height },
      normalAtlas: { width: normalCanvas.width, height: normalCanvas.height },
      normalVariants: NORMAL_VARIANTS,
    },
  };
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

/**
 * Bake every card texture. Idempotent — repeated calls return the same promise,
 * so any module may await it without coordinating.
 */
export function preloadCardTextures(options: CardTextureOptions = {}): Promise<void> {
  building ??= build(options).catch((error: unknown) => {
    building = null;
    throw error;
  });
  return building;
}

export function areCardTexturesReady(): boolean {
  return atlas !== null;
}

function requireAtlas(): CardAtlas {
  if (!atlas) {
    throw new Error('cardTexture: await preloadCardTextures() before using card textures');
  }
  return atlas;
}

export function getFaceAtlasSource(): TextureSource {
  return requireAtlas().faceSource;
}

export function getNormalAtlasSource(): TextureSource {
  return requireAtlas().normalSource;
}

export function getFaceUV(id: CardId): CardUV {
  // A hidden card has no face to bake: in a ranked round the server has not
  // said what it is. The back is the honest answer, and it is the only thing
  // that will ever be drawn for one. The throw below stays for real ids,
  // because a mistyped card should still be loud.
  if (isHiddenCard(id)) return requireAtlas().backUV;
  const uv = requireAtlas().faceUVs.get(id);
  if (!uv) throw new Error(`cardTexture: no baked face for "${id}"`);
  return uv;
}

export function getBackUV(): CardUV {
  return requireAtlas().backUV;
}

/** UV of the paper-tooth variant assigned to `id` (or the back). */
export function getNormalUV(id: CardId | 'back'): CardUV {
  const current = requireAtlas();
  const known = current.normalIndex.get(id);
  // The fallback is for hidden placeholders, which have no face to key on. A
  // real id that misses quietly takes the back's variant, so say so in dev.
  if (known === undefined && import.meta.env.DEV && !isHiddenCard(id)) {
    throw new Error(`cardTexture: no normal variant for "${id}"`);
  }
  const index = known ?? 0;
  return current.normalUVs[index];
}

export function getFaceTexture(id: CardId): Texture {
  if (isHiddenCard(id)) return requireAtlas().backTexture;
  const texture = requireAtlas().faceTextures.get(id);
  if (!texture) throw new Error(`cardTexture: no baked face for "${id}"`);
  return texture;
}

export function getBackTexture(): Texture {
  return requireAtlas().backTexture;
}

/** The two blur levels `card.ts` crossfades between as a card's lift grows. */
export function getShadowStamps(): { tight: ShadowStamp; soft: ShadowStamp } {
  const current = requireAtlas();
  return { tight: current.shadowTight, soft: current.shadowSoft };
}

export function getCardAtlasStats(): CardAtlasStats {
  return requireAtlas().stats;
}

/** True for J♠ and J♣ only. J♥ and J♦ are ordinary tens. */
export function isWildCard(id: CardId): boolean {
  return WILD_CARDS.includes(id);
}

/* ------------------------------------------------------------------ *
 * Ceremony plates
 * ------------------------------------------------------------------ */

/** A small atlas of full-size cards: the rank ceremony's props. */
export interface CardPlates {
  readonly faceSource: TextureSource;
  readonly normalSource: TextureSource;
  readonly faceUV: ReadonlyMap<CardId, CardUV>;
  readonly backUV: CardUV;
  readonly normalUV: CardUV;
  readonly cellWidth: number;
  readonly cellHeight: number;
}

const plates = new Map<string, Promise<CardPlates>>();

/**
 * Bake a handful of cards at ceremony resolution.
 *
 * The table's atlas (`preloadCardTextures`) is sized for a card the width of a
 * pile — about 200 design units. The rank ceremony's card fills half the short
 * side of the screen, so drawing it from that atlas is a 3x upscale of the one
 * card the player is being asked to look at. This bakes just the cards a
 * ceremony needs, at the size it needs them, through exactly the same pipeline:
 * the same generated paper, the same corner letters in the game's display face,
 * the same wild treatment, the same rules, and one normal cell so the stock is
 * lit per-pixel as it is on the table.
 *
 * Cached per (ids, width), because the card is the same one every time that
 * rank comes up and rasterising SVGs on the main thread is a freeze.
 */
export function bakeCardPlates(
  ids: readonly CardId[],
  cellWidth: number,
  /**
   * Which of these plates wear the wild treatment.
   *
   * On the table, J♠ and J♣ are wild and carry the band that says so. In a
   * ceremony the same card can be two different things: the **Wild** rank's
   * badge, where the band is the whole point, and the **Jack** rank's badge —
   * or the jack of a Blackjack pair — where a card stamped WILD would be
   * telling the player something untrue about their rank. So the treatment is
   * asked for per plate rather than read off the card's id, and it is part of
   * the cache key.
   */
  wild: readonly CardId[] = [],
): Promise<CardPlates> {
  const key = `${[...ids].sort().join(',')}@${cellWidth}+${[...wild].sort().join(',')}`;
  const cached = plates.get(key);
  if (cached) return cached;
  const built = buildPlates(ids, cellWidth, new Set(wild)).catch((error: unknown) => {
    plates.delete(key);
    throw error;
  });
  plates.set(key, built);
  return built;
}

async function buildPlates(
  ids: readonly CardId[],
  cellWidth: number,
  wild: ReadonlySet<CardId>,
): Promise<CardPlates> {
  if (typeof document === 'undefined') {
    throw new Error('cardTexture: bakeCardPlates() requires a DOM');
  }
  await ensureFonts();

  // Cell 0 is the back; the faces follow.
  const layout = planGrid(ids.length + 1, cellWidth, 4096, 13_000_000);
  const faceCanvas = makeCanvas(layout.width, layout.height);
  const faceCtx = context2d(faceCanvas);
  const radius = (CARD.cornerRadius / CARD.width) * layout.cellW;

  {
    const { x, y } = cellOrigin(0, layout);
    faceCtx.save();
    faceCtx.translate(x, y);
    roundRectPath(faceCtx, 0, 0, layout.cellW, layout.cellH, radius);
    faceCtx.clip();
    paintBack(faceCtx, layout.cellW, layout.cellH, radius);
    faceCtx.restore();
  }

  const images = await Promise.all(ids.map((id) => rasteriseSvg(id, layout.cellW, layout.cellH)));
  const faceUV = new Map<CardId, CardUV>();
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const image = images[i];
    const index = i + 1;
    const { x, y } = cellOrigin(index, layout);
    faceCtx.save();
    faceCtx.translate(x, y);
    roundRectPath(faceCtx, 0, 0, layout.cellW, layout.cellH, radius);
    faceCtx.clip();
    paintPaper(faceCtx, layout.cellW, layout.cellH);
    faceCtx.drawImage(image, 0, 0, layout.cellW, layout.cellH);
    drawCardIndices(faceCtx, layout.cellW, layout.cellH, id, tokens.type.display);
    if (wild.has(id)) {
      paintWildTreatment(faceCtx, layout.cellW, layout.cellH, radius);
    }
    paintCardRules(faceCtx, layout.cellW, layout.cellH, radius);
    faceCtx.restore();
    faceUV.set(
      id,
      uvRect(index % layout.cols, Math.floor(index / layout.cols), layout, faceCanvas.width, faceCanvas.height),
    );
  }

  // One tooth variant: a ceremony shows one card at a time, so neighbouring
  // stock never has to differ.
  const normalLayout: GridLayout = {
    cols: 1,
    rows: 1,
    cellW: layout.cellW,
    cellH: layout.cellH,
    pad: layout.pad,
    width: layout.cellW + layout.pad * 2,
    height: layout.cellH + layout.pad * 2,
  };
  const normalCanvas = makeCanvas(normalLayout.width, normalLayout.height);
  const normalCtx = context2d(normalCanvas);
  normalCtx.fillStyle = 'rgb(128, 128, 255)';
  normalCtx.fillRect(0, 0, normalCanvas.width, normalCanvas.height);
  const scale = layout.cellW / CARD.width;
  const cell = normalCtx.createImageData(layout.cellW, layout.cellH);
  buildNormalCell(
    cell,
    layout.cellW,
    layout.cellH,
    radius,
    BEVEL_DESIGN * scale,
    DOME_DESIGN * scale,
    TOOTH_COARSE * scale,
    TOOTH_FINE * scale,
    7_919,
  );
  const origin = cellOrigin(0, normalLayout);
  normalCtx.putImageData(cell, origin.x, origin.y);

  return {
    faceSource: new ImageSource({
      resource: faceCanvas,
      label: 'ceremony-face-plates',
      scaleMode: 'linear',
      addressMode: 'clamp-to-edge',
      autoGenerateMipmaps: false,
    }),
    normalSource: new ImageSource({
      resource: normalCanvas,
      label: 'ceremony-normal-plate',
      scaleMode: 'linear',
      addressMode: 'clamp-to-edge',
      autoGenerateMipmaps: false,
      alphaMode: 'no-premultiply-alpha',
    }),
    faceUV,
    backUV: uvRect(0, 0, layout, faceCanvas.width, faceCanvas.height),
    normalUV: uvRect(0, 0, normalLayout, normalCanvas.width, normalCanvas.height),
    cellWidth: layout.cellW,
    cellHeight: layout.cellH,
  };
}

/**
 * Bake cards whose faces are DRAWN rather than loaded from the deck — the
 * chest's reward cards — on exactly the deck's stock: the same generated
 * paper, the same rules, the same back, and a baked normal cell so the paper
 * is lit per-pixel like any other card.
 *
 * Each painter draws its face's ink onto the paper; the ceremony's card mesh
 * then strikes the paper in the rarity's finish and leaves the ink as ink,
 * which is what keeps a gold card's number readable.
 */
export async function bakeFacePlates(
  painters: ReadonlyArray<(ctx: CanvasRenderingContext2D, w: number, h: number) => void>,
  cellWidth: number,
  key: (index: number) => CardId,
): Promise<CardPlates> {
  if (typeof document === 'undefined') {
    throw new Error('cardTexture: bakeFacePlates() requires a DOM');
  }
  await ensureFonts();
  const layout = planGrid(painters.length + 1, cellWidth, 4096, 13_000_000);
  const faceCanvas = makeCanvas(layout.width, layout.height);
  const faceCtx = context2d(faceCanvas);
  const radius = (CARD.cornerRadius / CARD.width) * layout.cellW;

  const cell = (index: number, paint: (ctx: CanvasRenderingContext2D) => void): void => {
    const { x, y } = cellOrigin(index, layout);
    faceCtx.save();
    faceCtx.translate(x, y);
    roundRectPath(faceCtx, 0, 0, layout.cellW, layout.cellH, radius);
    faceCtx.clip();
    paint(faceCtx);
    faceCtx.restore();
  };

  cell(0, (ctx) => paintBack(ctx, layout.cellW, layout.cellH, radius));
  const faceUV = new Map<CardId, CardUV>();
  painters.forEach((paint, i) => {
    const index = i + 1;
    cell(index, (ctx) => {
      paintPaper(ctx, layout.cellW, layout.cellH);
      paint(ctx, layout.cellW, layout.cellH);
      paintCardRules(ctx, layout.cellW, layout.cellH, radius);
    });
    faceUV.set(
      key(i),
      uvRect(index % layout.cols, Math.floor(index / layout.cols), layout, faceCanvas.width, faceCanvas.height),
    );
  });

  const normalLayout: GridLayout = {
    cols: 1,
    rows: 1,
    cellW: layout.cellW,
    cellH: layout.cellH,
    pad: layout.pad,
    width: layout.cellW + layout.pad * 2,
    height: layout.cellH + layout.pad * 2,
  };
  const normalCanvas = makeCanvas(normalLayout.width, normalLayout.height);
  const normalCtx = context2d(normalCanvas);
  normalCtx.fillStyle = 'rgb(128, 128, 255)';
  normalCtx.fillRect(0, 0, normalCanvas.width, normalCanvas.height);
  const scale = layout.cellW / CARD.width;
  const normal = normalCtx.createImageData(layout.cellW, layout.cellH);
  buildNormalCell(
    normal, layout.cellW, layout.cellH, radius,
    BEVEL_DESIGN * scale, DOME_DESIGN * scale, TOOTH_COARSE * scale, TOOTH_FINE * scale, 4_241,
  );
  const origin = cellOrigin(0, normalLayout);
  normalCtx.putImageData(normal, origin.x, origin.y);

  return {
    faceSource: new ImageSource({
      resource: faceCanvas, label: 'reward-face-plates', scaleMode: 'linear',
      addressMode: 'clamp-to-edge', autoGenerateMipmaps: false,
    }),
    normalSource: new ImageSource({
      resource: normalCanvas, label: 'reward-normal-plate', scaleMode: 'linear',
      addressMode: 'clamp-to-edge', autoGenerateMipmaps: false, alphaMode: 'no-premultiply-alpha',
    }),
    faceUV,
    backUV: uvRect(0, 0, layout, faceCanvas.width, faceCanvas.height),
    normalUV: uvRect(0, 0, normalLayout, normalCanvas.width, normalCanvas.height),
    cellWidth: layout.cellW,
    cellHeight: layout.cellH,
  };
}
